import { Grid, TextField } from '@mui/material';
import { RHFAutocomplete } from 'src/components/minimals/form';
import DateAggregationSelect from '../sidebar/form-inputs/DateAggregationSelect';
import {
  AGGREGATION_SELECT_OPTIONS,
  DATA_AGGREGATION_OPTIONS,
  NO_DATE_LIMIT_AGGREGATION_INTERVALS,
} from '../../config/config';
import { useFormContext } from 'react-hook-form';
import { ITimezoneOption } from 'react-timezone-select';
import { TimeFrameRangePicker } from '../sidebar/form-inputs/TimeFrameRangePicker';
import { timeFrameOptions } from '../../utils/timeFrameToDateUtils';
import TimeFrameDropdown from '../sidebar/form-inputs/TimeFrameDropdown';
import dayjs from 'dayjs';
import { useState } from 'react';

type DataReportBaseLevelFormItemsProps = {
  testId: string;
  options: ITimezoneOption[];
  isMobile: boolean;
  setError: (error: boolean) => void;
};
export const DataReportBaseLevelFormItems = ({
  testId,
  options,
  isMobile,
  setError,
}: DataReportBaseLevelFormItemsProps) => {
  const { setValue, watch } = useFormContext();
  const [showTimeFrameError, setShowTimeFrameError] = useState<boolean>(false);

  const xlGridSize = 2;

  const isOverSixMonths = (timeFrame: any): boolean => {
    if (!Array.isArray(timeFrame) || timeFrame.length !== 2) return false;
    const [start, end] = timeFrame;
    return dayjs(end).diff(dayjs(start), 'month', true) > 6;
  };

  return (
    <>
      <Grid item xs={12} sm={12} md={4} xl={xlGridSize}>
        <TextField
          value="Data"
          disabled={true}
          size="small"
          sx={{ width: '100%' }}
          label="Report Type"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4} xl={xlGridSize}>
        <RHFAutocomplete
          name="data.timeZone"
          isOptionEqualToValue={(option, value) => option.value === value.value}
          rules={{ required: 'The field is required' }}
          options={options}
          disableClearable
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                'data-sm': testId,
              }}
              label={'Time Zone *'}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4} xl={xlGridSize}>
        <TimeFrameDropdown
          value={watch('data.timeFrame')}
          onChange={(val: any) => {
            const agg = watch('data.aggregationInterval');
            const isShortInterval = !NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(agg);
            const isInvalid = isShortInterval && isOverSixMonths(val);

            setShowTimeFrameError(isInvalid);
            setError(isInvalid);
            setValue('data.timeFrame', val);
          }}
          testId="asset-report-time-frame"
          timeFrames={timeFrameOptions}
          error={
            showTimeFrameError
              ? 'For aggregation intervals under 30 minutes the report time frame cannot be more than 6 months.'
              : undefined
          }
        />
      </Grid>

      {Array.isArray(watch('data.timeFrame')) && (
        <Grid item xs={12} sm={12} md={6} xl={xlGridSize}>
          <TimeFrameRangePicker
            value={watch('data.timeFrame')}
            isMobile={isMobile}
            onChange={(val) => {
              const agg = watch('data.aggregationInterval');
              const isShortInterval = !NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(agg);
              const isInvalid = isShortInterval && isOverSixMonths(val);

              setShowTimeFrameError(isInvalid);
              setError(isInvalid);
              setValue('data.timeFrame', val);
            }}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={12} md={6} xl={xlGridSize}>
        <DateAggregationSelect
          testId="data-report-aggregation-interval"
          value={watch('data.aggregationInterval')}
          onChange={(event) => {
            const newVal = event.target!.value as string;
            const timeFrame = watch('data.timeFrame');
            const isShortInterval = !NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(newVal);
            const isInvalid = isShortInterval && isOverSixMonths(timeFrame);
            setShowTimeFrameError(isInvalid);
            setError(isInvalid);
            const items = watch('data.columns.custom');
            if (event.target!.value === 'NONE') {
              const updatedItems = items.map((obj: any) => ({
                ...obj,
                aggregation: null,
              }));
              setValue('data.columns.custom', updatedItems);
            } else if (event.target!.value !== 'NONE') {
              const updatedItems = items.map((obj: any) =>
                obj.aggregation === null
                  ? { ...obj, aggregation: AGGREGATION_SELECT_OPTIONS[1] }
                  : obj
              );
              setValue('data.columns.custom', updatedItems);
            }
            setValue('data.aggregationInterval', newVal);
          }}
          timeFrames={DATA_AGGREGATION_OPTIONS}
        />
      </Grid>
    </>
  );
};
